import React from "react";
import {
  Flex,
  Text,
  Avatar,
  Pill,
  PillWrapper,
  Grid,
  calculateUserColor
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { PlanObjectiveColors } from "@/Utils/Plan/ObjectivesHandler";
import { TruncatedText } from "../../Utils/TruncatedText/TruncatedText";
import ReviewerSummary from "../PlanApprovalStatusModal/ReviewerSummary";

const DetailRow = ({ label, children, alignItems = "center", style = {} }) => (
  <>
    <Text
      size={"small"}
      modifier={"bold"}
      colorModifier={"grey"}
      style={{ alignSelf: alignItems, justifySelf: "start" }}
    >
      {label}
    </Text>
    <div style={{ alignSelf: alignItems }}>{children}</div>
  </>
);

const UserDisplay = ({ username }) => {
  if (!username) return null;

  return (
    <Flex gap={"8px"} alignItems={"center"}>
      <Avatar
        text={username[0].toUpperCase()}
        isTriggerButton={false}
        color={calculateUserColor(username[0].toUpperCase())}
        size={"small"}
      />
      <Text size={"small"}>{username}</Text>
    </Flex>
  );
};

/**
 * Grid wrapper for plan details sections
 */
export const PlanDetailsGrid = ({ children }) => (
  <Grid gridColumns="auto 1fr" rowGap={12} columnGap={24}>
    <Text size={"medium"} modifier={"bold"} style={{ gridColumn: "1 / span 2", marginBottom: 4 }}>
      {appstr("Plan details")}
    </Text>
    {children}
  </Grid>
);

/**
 * Displays plan information section (Name, Owner, Objective, Description)
 * @param {Object} planInfo - The plan metadata object
 * @param {boolean} showName - Whether to show the plan name field
 */
export const PlanInfoSection = ({ planInfo, showName = false }) => {
  if (!planInfo) return null;

  const objectiveColor = planInfo.objective ? PlanObjectiveColors[planInfo.objective] : null;
  const planOwnerDisplayName = planInfo.owner
    ? (planInfo.users?.find(user => user.name === planInfo.owner)?.displayName ?? planInfo.owner)
    : null;

  return (
    <>
      {showName && planInfo.name && (
        <DetailRow label={appstr("Name")}>
          <Text size={"small"} modifier={"medium"}>
            {planInfo.name}
          </Text>
        </DetailRow>
      )}

      {planOwnerDisplayName && (
        <DetailRow label={appstr("Owner")}>
          <UserDisplay username={planOwnerDisplayName} />
        </DetailRow>
      )}

      {planInfo.objective && (
        <DetailRow label={appstr("Objective")}>
          <Pill
            className="oc-relative"
            size={"regular"}
            label={appstr(planInfo.objective)}
            pillColors={objectiveColor}
          />
        </DetailRow>
      )}

      {planInfo.description && (
        <DetailRow label={appstr("Description")} alignItems={"flex-start"}>
          <Text size={"small"}>{planInfo.description}</Text>
        </DetailRow>
      )}
    </>
  );
};

/**
 * Displays plan submission details (Submitted by, Submitted on, Feedback)
 */
export const SubmissionInfoSection = ({ submission }) => {
  if (!submission) return null;

  const submissionDate = submission.date;
  const submitterName = submission.displayName || submission.username;

  return (
    <>
      {submitterName && (
        <DetailRow label={appstr("Submitted by")}>
          <UserDisplay username={submitterName} />
        </DetailRow>
      )}

      {submissionDate && (
        <DetailRow label={appstr("Submitted on")}>
          <Text size={"small"}>{new Date(submissionDate + "Z").toLocaleString()}</Text>
        </DetailRow>
      )}

      {submission.message && (
        <DetailRow label={appstr("Submission message")} alignItems={"flex-start"}>
          <TruncatedText text={submission.message} maxLines={4} />
        </DetailRow>
      )}
    </>
  );
};

/**
 * Displays reviewers section
 */
export const ReviewersSection = ({ reviewers }) => {
  if (!reviewers || reviewers.length === 0) return null;

  return (
    <Flex direction={"column"} gap={"8px"}>
      <Text size={"medium"} modifier={"bold"}>
        {appstr("Approvers label")}
      </Text>
      <Flex direction={"column"} gap={"4px"}>
        {reviewers.map(reviewer => (
          <ReviewerSummary key={reviewer.username} reviewer={reviewer} />
        ))}
      </Flex>
    </Flex>
  );
};
